import { z } from 'zod';
import { defaultActionMustBeAvailableRefinement, hasValidDefaultAction, } from '../legislative-actions/index.js';
import { numberingStyleSchema } from '../shared/index.js';
export const LegislativeTypeStatus = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
};
export const PublicHearing = {
    REQUIRED: 'required',
    NOT_APPLICABLE: 'not_applicable',
    OPTIONAL: 'optional',
};
/**
 * Per-item-type attachment numbering configuration. When set, attachments on items of this type
 * can be auto-numbered (counter resets per legislative item). A null/absent value disables
 * attachment numbering for the item type. The number is rendered before the attachment name with a
 * single space; any extra punctuation belongs in the format token (e.g. "Exhibit {n}:").
 */
export const attachmentNumberingConfigSchema = z.object({
    formatToken: z
        .string()
        .min(1, 'Format is required')
        .max(30)
        .refine((value) => value.includes('{n}'), 'Format must contain the {n} token')
        .describe('Format template containing the {n} token, e.g. "Exhibit {n}" or "Attachment {n}".'),
    style: numberingStyleSchema
        .refine((style) => style !== 'none', 'Choose a numbering style')
        .describe('Numbering style used to render {n}.'),
});
/**
 * Field shape shared by the create/update payloads and by callers that need the
 * raw object without the default-action refinement — notably the support MCP
 * tool, which derives its input schema from `.partial().shape`.
 */
export const legislativeTypeBaseSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255).describe('Required for create'),
    code: z.string().min(1, 'Code is required').max(255).describe('Required for create'),
    description: z.string().nullish(),
    status: z.enum(LegislativeTypeStatus).describe('Required for create'),
    orderById: z.number().min(1, 'Display order must be at least 1').describe('Required for create'),
    consentEligible: z.boolean().optional(),
    emergencyEligible: z.boolean().optional(),
    executiveSession: z.boolean().optional(),
    timeEstimate: z.boolean().optional(),
    defaultRequestingConsent: z
        .boolean()
        .optional()
        .describe('Default value for a new item\'s "Requesting Consent" flag. Requires consentEligible.'),
    defaultRequestingEmergency: z
        .boolean()
        .optional()
        .describe('Default value for a new item\'s "Requesting Emergency" flag. Requires emergencyEligible.'),
    defaultExecutiveSession: z
        .boolean()
        .optional()
        .describe('Default value for a new item\'s "Executive Session" flag. Requires executiveSession.'),
    defaultPublicHearing: z
        .boolean()
        .optional()
        .describe('Default value for a new item\'s "Public Hearing" flag. Requires publicHearing to not be not_applicable.'),
    showSummary: z.boolean().optional(),
    showFiscalNote: z.boolean().optional(),
    showSponsors: z.boolean().optional(),
    showRecommendedAction: z.boolean().optional(),
    showRecommendedActionText: z.boolean().optional(),
    noticePeriod: z
        .number()
        .min(0, 'Notice period must be non-negative')
        .describe('Required for create. Days of advance notice required before this item type can appear on an agenda.'),
    publicHearing: z.enum(PublicHearing).describe('Required for create'),
    actionIds: z
        .array(z.number())
        .optional()
        .describe('IDs of legislative actions available on items of this type.'),
    defaultActionId: z
        .number()
        .nullish()
        .describe('Default legislative action ID. Must be one of actionIds.'),
    autoNumberingConfigId: z.number().nullish(),
    workflowTemplateId: z
        .number()
        .min(1, 'Workflow template is required')
        .describe('The ID of the legislative item workflow template to be used for items of this type.'),
    attachmentNumbering: attachmentNumberingConfigSchema
        .nullish()
        .describe('Attachment numbering config for items of this type. Null/omitted disables attachment numbering.'),
});
export const legislativeTypeCreateSchema = legislativeTypeBaseSchema.refine(hasValidDefaultAction, defaultActionMustBeAvailableRefinement);
export const legislativeTypeDocumentUpdateSchema = z.array(z.object({
    documentTypeId: z.number().min(1, 'Document type is required'),
    isRequired: z.boolean(),
    autoCreateOnSubmit: z.boolean().optional(),
    orderById: z.number(),
}));
export const legislativeTypeAttachmentUpdateSchema = z.array(z.object({
    attachmentTypeId: z.number().min(1, 'Attachment type is required'),
    isRequired: z.boolean(),
    orderById: z.number(),
}));
/**
 * Update payloads are partial: every field an item type already stores keeps its
 * value when omitted, so a caller sends only what it means to change.
 *
 * The default-action membership rule is deliberately not refined here. It
 * depends on the row being updated — omitting `actionIds` leaves the stored
 * action set in place — so the update controller checks it against the merged
 * record instead.
 */
export const legislativeTypeUpdateSchema = legislativeTypeBaseSchema.partial();
